import { useCallback, useState } from 'react'

import { isValidationError } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

type SubmitError = Parameters<typeof isValidationError>[0]

/**
 * Message shown when creating a campaign fails. A validation error from the
 * API means required information is missing; anything else keeps the
 * generic message.
 *
 * `resolveSubmitError` records the failure and returns its message right away,
 * for the toast fired in the same handler. `submitErrorMessage` is that same
 * message read on the next render, for the alert title. Both come from the one
 * recorded failure, so they cannot disagree.
 */
export const useSubmitErrorMessage = () => {
  const { t } = useAPPTranslation()
  const [isValidationFailure, setIsValidationFailure] = useState(false)

  const validationMessage = t(
    'rm:campaign.create.toast.error.validationMessage'
  )
  const genericMessage = t('rm:campaign.create.toast.error.message')

  const resolveSubmitError = useCallback(
    (error: SubmitError) => {
      const validationFailure = isValidationError(error)

      setIsValidationFailure(validationFailure)

      return validationFailure ? validationMessage : genericMessage
    },
    [validationMessage, genericMessage]
  )

  return {
    submitErrorMessage: isValidationFailure
      ? validationMessage
      : genericMessage,
    resolveSubmitError
  }
}
